import React, { useState } from "react";
import {
  TextField,
  Box,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import AccountSearchBox, { NEW_ACCOUNT_ID } from "./AccountSearchBox";
import { ContactDetails, ZohoAccount } from "../../types/zoho";
import axios, { AxiosError } from "axios";
import { LAMBDA_BASE_URL } from "../../constants";
import { LoadingButton } from "@mui/lab";
interface Props {
  onSuccess: (contactDetails: ContactDetails) => void;
}
const PERSONAL_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "aol.com",
  "icloud.com",
  "mail.com",
  "protonmail.com",
  "zoho.com",
  "yandex.com",
];

const getAccountNameFromEmail = (email: string): string | null => {
  const domain = email.split("@")[1];
  if (domain && !PERSONAL_EMAIL_DOMAINS.includes(domain)) {
    return domain.split(".")[0];
  }
  return null;
};

export function CreateContact({ onSuccess }: Props) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fullName = queryParams.get("fullName") || "";
  const email = queryParams.get("email") || "";

  // State variables for form inputs
  const [contactName, setContactName] = useState<string>(
    fullName ? fullName : ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorOnCreate, setErrorOnCreate] = useState<null | string>(null);
  const [account, setAccount] = useState<ZohoAccount | null>(null);
  const [primaryEmail, setPrimaryEmail] = useState<string>(email);
  const [secondaryEmail, setSecondaryEmail] = useState<string>("");
  const [linkedInUrl, setLinkedInUrl] = useState<string>("");
  const [ownedByCP, setOwnedByCP] = useState<boolean>(false);
  const isLinkedInRequired = PERSONAL_EMAIL_DOMAINS.includes(
    primaryEmail.split("@")[1]
  );
  const isLinkedInValid =
    linkedInUrl.length === 0
      ? true
      : /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-_]+\/?$/.test(
          linkedInUrl.trim()
        );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorOnCreate(null);
    try {
      await axios.post<any>(
        LAMBDA_BASE_URL,
        {
          contactName,
          email: primaryEmail,
          secondaryEmail,
          accountName: account!.Account_Name,
          linkedInUrl: linkedInUrl.trim(),
          isNewAccount: account!.id === NEW_ACCOUNT_ID,
          ownedByCP,
        },
        {
          params: {
            isCreate: true,
          },
        }
      );
      setIsLoading(false);
      onSuccess({
        fullName: contactName.trim(),
        account: account!.Account_Name,
      });
    } catch (error) {
      setIsLoading(false);
      setErrorOnCreate(
        "Something went wrong while creating the contact. Error message: " +
          (!account
            ? "Account must be selected."
            : (error as AxiosError<{ message: string }>).response?.data
                ?.message || "Unknown error")
      );
      console.error("Error creating contact", error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mt: 3 }} display='flex' flexDirection='column' gap={2}>
        <TextField
          required
          disabled={isLoading}
          label='Contact name'
          variant='outlined'
          fullWidth
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
        />
        <Stack direction='row' gap={2}>
          <TextField
            required
            disabled={isLoading}
            label='Primary Email'
            variant='outlined'
            fullWidth
            value={primaryEmail}
            onChange={(e) => setPrimaryEmail(e.target.value)}
          />
          <TextField
            disabled={isLoading}
            label='Secondary Email'
            variant='outlined'
            fullWidth
            value={secondaryEmail}
            onChange={(e) => setSecondaryEmail(e.target.value)}
          />
        </Stack>
        <TextField
          required={isLinkedInRequired}
          disabled={isLoading}
          label='Contact LinkedIn Url'
          variant='outlined'
          fullWidth
          value={linkedInUrl}
          onChange={(e) => setLinkedInUrl(e.target.value)}
          error={!isLinkedInValid}
          helperText={
            !isLinkedInValid
              ? "LinkedIn URL must be like: https://www.linkedin.com/in/username/"
              : ""
          }
        />

        <AccountSearchBox
          onChange={(account) => setAccount(account)}
          accountNameFromEmail={getAccountNameFromEmail(email)}
          selectedAccount={account || null}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={ownedByCP}
              onChange={(e) => setOwnedByCP(e.target.checked)}
              disabled={isLoading}
            />
          }
          label='Owned by CP'
        />
        <LoadingButton
          disabled={isLoading || !isLinkedInValid}
          type='submit'
          variant='contained'
          color='primary'
          size='large'
          loading={isLoading}>
          Create
        </LoadingButton>
        {errorOnCreate && (
          <Typography variant='body1' color='error' mt='8px'>
            {errorOnCreate}
          </Typography>
        )}
      </Box>
    </form>
  );
}
